import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, Volume2, VolumeX } from 'lucide-react';
import { AudioManager } from '../../lib/AudioManager';
import { CONSTANTS } from '../../data';
import { usePalette } from '../../PaletteContext';

const SettingsModal = ({ 
  onClose, 
  audioManager 
}: { 
  onClose: () => void; 
  audioManager: AudioManager;
}) => {
  const [musicVolume, setMusicVolume] = useState(audioManager.getMusicVolume());
  const [sfxVolume, setSfxVolume] = useState(audioManager.getSfxVolume());
  const [isMuted, setIsMuted] = useState(audioManager.getIsMuted());

  const { paletteKey, setPaletteKey } = usePalette();

  const handleMusicVolumeChange = (value: number) => {
    setMusicVolume(value);
    audioManager.setMusicVolume(value);
  };

  const handleSfxVolumeChange = (value: number) => {
    setSfxVolume(value);
    audioManager.setSfxVolume(value);
    audioManager.playSound('buttonClick'); // Test sound
  };

  const handleMuteToggle = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    audioManager.setMuted(newMuted);
  };

  // Palette selection
  const palettes = CONSTANTS.COLOR_PALETTES;
  const paletteOptions = Object.values(palettes);

  return (
    <motion.div
      className="fixed inset-0 z-[5500] flex items-center justify-center backdrop-blur-sm"
      style={{
        background: `linear-gradient(135deg, var(--color-primary), var(--color-primary-light), var(--color-background))`
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden backdrop-blur-sm border-2"
        style={{
          background: `linear-gradient(135deg, var(--color-primary-dark), var(--color-primary))`,
          color: 'var(--color-text)',
          borderColor: 'var(--color-accent)'
        }}
        initial={{ scale: 0.8, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Header */}
        <div className="p-6 relative overflow-hidden" style={{
          background: `linear-gradient(to right, var(--color-primary), var(--color-primary-light))`
        }}>
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
          <div className="relative z-10">
            <motion.div
              initial={{ scale: 0.8, rotate: -10, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 120, damping: 10 }}
              className="absolute top-4 right-4"
            >
              <Settings className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
            </motion.div>
            
            <motion.h2 
              className="text-3xl font-extrabold text-center drop-shadow-lg tracking-wide"
              style={{ color: 'var(--color-accent)' }}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {CONSTANTS.UI_TEXT.SETTINGS_TITLE}
            </motion.h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Mute Toggle */}
          <motion.div
            className="flex items-center justify-between"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <span className="text-lg font-semibold">{CONSTANTS.UI_TEXT.MUTE_AUDIO_LABEL}</span>
            <motion.button
              onClick={handleMuteToggle}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all"
              style={{
                background: isMuted ? '#ef4444' : 'var(--color-secondary)',
                color: 'var(--color-text)'
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              {isMuted ? CONSTANTS.UI_TEXT.UNMUTE_BUTTON : CONSTANTS.UI_TEXT.MUTE_BUTTON}
            </motion.button>
          </motion.div>

          {/* Music Volume */}
          <motion.div
            className="space-y-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">{CONSTANTS.UI_TEXT.MUSIC_VOLUME_LABEL}</span>
              <span className="font-bold" style={{ color: 'var(--color-accent)' }}>{Math.round(musicVolume * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={musicVolume}
              onChange={(e) => handleMusicVolumeChange(parseFloat(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer"
              style={{ background: 'var(--color-primary)' }}
              disabled={isMuted}
            />
          </motion.div>

          {/* SFX Volume */}
          <motion.div
            className="space-y-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">{CONSTANTS.UI_TEXT.SFX_VOLUME_LABEL}</span>
              <span className="font-bold" style={{ color: 'var(--color-accent)' }}>{Math.round(sfxVolume * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={sfxVolume}
              onChange={(e) => handleSfxVolumeChange(parseFloat(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer"
              style={{ background: 'var(--color-primary)' }}
              disabled={isMuted}
            />
          </motion.div>

          {/* Color Palette Selector */}
          <motion.div
            className="space-y-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">{CONSTANTS.UI_TEXT.COLOR_PALETTE_LABEL}</span>
              <span className="font-bold" style={{ color: 'var(--color-accent)' }}>
                {palettes[paletteKey]?.name || CONSTANTS.UI_TEXT.COLOR_PALETTE_DEFAULT}
              </span>
            </div>
            <div className="flex gap-3 flex-wrap">
              {paletteOptions.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPaletteKey(p.key)}
                  className={`flex flex-col items-center px-2 py-2 rounded-xl border-2 transition-all duration-200
                    ${paletteKey === p.key
                      ? 'ring-2 scale-105'
                      : 'hover:scale-105'
                    }
                  `}
                  style={{
                    background: `linear-gradient(135deg, ${p.primary}, ${p.primaryLight}, ${p.background})`,
                    borderColor: paletteKey === p.key ? p.accent : 'rgba(255, 255, 255, 0.2)',
                    ringColor: paletteKey === p.key ? p.accent : 'transparent'
                  }}
                  type="button"
                >
                  <div className="flex gap-1 mb-1">
                    <span
                      className="w-4 h-4 rounded-full border border-white"
                      style={{ background: p.primary }}
                    ></span>
                    <span
                      className="w-4 h-4 rounded-full border border-white"
                      style={{ background: p.secondary }}
                    ></span>
                    <span
                      className="w-4 h-4 rounded-full border border-white"
                      style={{ background: p.accent }}
                    ></span>
                  </div>
                  <span className="text-xs font-bold text-white/90">{p.name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t" style={{
          background: 'var(--color-primary-dark)',
          borderColor: 'var(--color-accent)'
        }}>
          <motion.button
            onClick={onClose}
            className="w-full px-8 py-3 rounded-xl font-extrabold text-lg shadow-lg transition-all border-2 hover:scale-105 active:scale-95"
            style={{
              background: 'var(--color-button)',
              color: 'var(--color-button-text)',
              borderColor: 'var(--color-accent)'
            }}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, type: "spring", stiffness: 200, damping: 12 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {CONSTANTS.UI_TEXT.CLOSE_BUTTON}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SettingsModal;
